import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import Links from '@/shared/constants/navegacao';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { Link, useParams } from 'react-router-dom';
import { CandidatoService } from './candidato.service';
import { ReadCandidatoDto } from '@/api.types';
import { ProcessoSeletivoService } from '../processo-seletivo/processo-seletivo.service';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useState } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { validaCPF } from '@/shared/utils/cpfs';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();

export default function CandidatoIndexPage() {
  const { setActions } = useBreadcrumbActions();

  const params = useParams();
  const processoSeletivoId = params.id ? Number(params.id) : undefined;

  const [registros, setRegistros] = useState<[ReadCandidatoDto[], number]>();
  const [processoSeletivo, setProcessoSeletivo] = useState<{ nome: string } | null>(null);
  const [modalExcluir, setModalExcluir] = useState<{ id: number; nome: string } | null>(null);
  const tableState = useDataTableState('dataInscricao');

  useEffect(() => {
    if (processoSeletivoId) {
      // Carregar informações do processo seletivo
      processoSeletivoService.findOne(processoSeletivoId)
        .then((result) => {
          setProcessoSeletivo({ nome: result.nome });
        })
        .catch((error) => {
          console.error('Falha ao carregar processo seletivo:', error);
        });
    }
  }, [processoSeletivoId]);

  useEffect(() => {
    if (!processoSeletivoId) return;
    setActions({
      secondary: {
        label: 'Gestão de Arquivos',
        href: `${Links.Candidato_Gestao_Arquivos}/${processoSeletivoId}`,
        onClick: () => { },
      },
    });
    return () => setActions({});
  }, [processoSeletivoId, setActions]);

  const handleBuscar = () => {
    candidatoService.findAll({
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      where: tableState.filter || undefined,
      orderBy: `${tableState.sortConfig.key} ${tableState.sortConfig.direction}`,
    }, processoSeletivoId).then((result) => {
      setRegistros(result);
    }).catch((res) => {
      toast.error(String(res) || '');
    });
  };

  useEffect(() => {
    if (processoSeletivoId) {
      handleBuscar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processoSeletivoId]);

  const formatarData = (data: Date | string) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatarSituacao = (situacao: string) => {
    const situacaoMap: Record<string, string> = {
      'rascunho': 'Rascunho',
      'inscricao_realizada': 'Inscrição Realizada',
      'inscricao_validada': 'Inscrição Validada',
      'indeferida': 'Indeferida',
      'classificada': 'Classificada',
      'convocada': 'Convocada',
    };
    return situacaoMap[situacao] || situacao;
  };

  const formatarNomePessoa = (nome?: string) => {
    if (!nome || nome.trim() === '') return '-';
    // Normaliza para minúsculas e coloca a primeira letra de cada palavra em maiúscula
    return nome
      .toLowerCase()
      .split(' ')
      .filter((p) => p.trim() !== '')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  };

  const formatarRaca = (raca?: string) => {
    if (!raca) return '-';
    const mapa: Record<string, string> = {
      'branca': 'Branca',
      'preta': 'Preta',
      'parda': 'Parda',
      'amarela': 'Amarela',
      'indigena': 'Indígena',
    };
    return mapa[raca.toLowerCase()] || raca;
  };

  const formatarSimNao = (valor?: boolean) => {
    if (valor === true) return 'sim';
    if (valor === false) return 'não';
    return '-';
  };

  const formatarPcd = (pessoaComDeficiencia?: boolean, optaVagasPcd?: boolean) => {
    if (optaVagasPcd === true) return 'PCD (concorrendo às vagas)';
    if (pessoaComDeficiencia === true) return 'PCD';
    if (pessoaComDeficiencia === false || optaVagasPcd === false) return 'Não';
    return '-';
  };

  // const handleExcluir = async (id: number, nome: string) => {
  //   if (!window.confirm(`Tem certeza que deseja excluir o candidato "${nome}"?\n\nEsta ação não pode ser desfeita.`)) {
  //     return;
  //   }

  //   try {
  //     await candidatoService.delete(id);
  //     toast.success('Candidato excluído com sucesso!');
  //     handleBuscar();
  //   } catch (error: any) {
  //     toast.error(error?.message || 'Erro ao excluir candidato.');
  //   }
  // };

  let colunas: Column<ReadCandidatoDto>[] = [
    {
      key: "pessoaNome",
      header: "Nome",
      render: (row) => formatarNomePessoa(row.pessoaNome),
    },
    {
      key: "pessoaCpf",
      header: "CPF",
      invisible: true,
      render: (row) => row.pessoaCpf ? validaCPF(row.pessoaCpf, true) : '-',
    },
    {
      key: "pessoaEmail",
      header: "E-mail",
      invisible: true,
      render: (row) => row.pessoaEmail || '-',
    },
    {
      key: "pessoaTelefone",
      header: "Telefone",
      invisible: true,
      render: (row) => row.pessoaTelefone || '-',
    },
    {
      key: "municipio",
      header: "Município",
      render: (row) => row.municipio || '-',
    },
    {
      key: "raca",
      header: "Identificação Racial",
      render: (row) => formatarRaca(row.raca),
    },
    {
      key: "optaVagasNegros",
      header: "Opta vagas pessoas negras",
      render: (row) => formatarSimNao(row.optaVagasNegros),
    },
    {
      key: "pessoaComDeficiencia",
      header: "PCD",
      render: (row) => formatarPcd(row.pessoaComDeficiencia, row.optaVagasPcd),
    },
    {
      key: "optaVagasPcd",
      header: "Opta vagas PCD",
      render: (row) => formatarSimNao(row.optaVagasPcd),
    },
    {
      key: "cursoPretendidoNome",
      header: "Curso",
      render: (row) => row.cursoPretendidoNome || '-',
    },
    {
      key: "dataInscricao",
      header: "Data de Inscrição",
      render: (row) => formatarData(row.dataInscricao),
    },
    {
      key: "situacao",
      header: "Situação",
      render: (row) => formatarSituacao(row.situacao),
    },
    {
      key: "acoes",
      header: "Ações",
      render: (row) => (
        <div className="d-flex gap-2">
          <Link className="link-secondary" to={`/candidato/${row.id}`}>
            Ver Detalhes
          </Link>
          {/* <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => setModalExcluir({ id: row.id, nome: row.pessoaNome || 'Candidato' })}
            title="Excluir candidato"
          >
            <i className="bi bi-trash" />
          </button> */}
        </div>
      ),
    },
  ];

  return (
    <div className="row">
      <div className="col-md-8">
        <h3 className="pt-4">
          Candidatos
          {processoSeletivo && ` - ${processoSeletivo.nome}`}
        </h3>
        <span className="blockquote-footer pb-4 text-end">
          Listagem de candidatos inscritos no processo seletivo.
        </span>
      </div>
      <div className="col-md-4">
        <br />
        <div className="float-end d-flex gap-2">
          <Link
            type="button"
            className="btn btn-secondary"
            to={Links.ProcessoSeletivo_Index}
          >
            Voltar
          </Link>
        </div>
      </div>
      <hr className="pb-4" />

      {registros && (
        <DataTable
          columns={colunas}
          data={registros[0]}
          initialSortKey="dataInscricao"
          onChange={handleBuscar}
          serverSide={true}
          states={tableState}
          totalRecords={registros[1]}
        />
      )}

      <ModalExcluir
        show={modalExcluir !== null}
        // handleExcluir={handleConfirmarExcluir}
        handleHide={() => setModalExcluir(null)}
        mensagemPersonalizada={
          modalExcluir
            ? `Tem certeza que deseja excluir o candidato "${modalExcluir.nome}"? Esta ação não pode ser desfeita.`
            : undefined
        }
      />
    </div>
  );
}

